/**
 * S3ConfigPage
 *
 * Configuration page for the S3 Plugin per agent.
 * Allows operators to configure bucket connection, credentials,
 * allowed file types, and path prefix restrictions.
 *
 * Validates: Requirements 20.1, 20.2, 20.5, 26.1, 26.2
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiGet, apiPost, apiPut, apiDelete, TENANT_TOKEN_KEY } from '../../api/client';
import { Card, Button, Input, Modal } from '../../design-system';
import { tokens } from '../../design-system/tokens';

// ─── Types ───────────────────────────────────────────────────────────────────

interface S3Config {
  id: string;
  virtualAgentId: string;
  bucketName: string;
  region: string;
  authMode: 'access-keys' | 'iam-role';
  accessKey?: string;
  secretKey?: string;
  basePrefix: string;
  presignedUrlExpiration: number;
  allowedMimeTypes: string[];
  allowedPrefixes: string[];
  blockedPrefixes: string[];
  searchableMetadataFields: string[];
  createdAt: string;
  updatedAt: string;
}

interface FormData {
  bucketName: string;
  region: string;
  authMode: 'access-keys' | 'iam-role';
  accessKey: string;
  secretKey: string;
  basePrefix: string;
  presignedUrlExpiration: number;
  allowedMimeTypes: string[];
  allowedPrefixes: string[];
  blockedPrefixes: string[];
  searchableMetadataFields: string[];
}

const AWS_REGIONS = [
  'us-east-1',
  'us-east-2',
  'us-west-1',
  'us-west-2',
  'eu-west-1',
  'eu-west-2',
  'eu-west-3',
  'eu-central-1',
  'eu-north-1',
  'ap-southeast-1',
  'ap-southeast-2',
  'ap-northeast-1',
  'ap-northeast-2',
  'ap-south-1',
  'sa-east-1',
  'ca-central-1',
];

const EMPTY_FORM: FormData = {
  bucketName: '',
  region: 'us-east-1',
  authMode: 'access-keys',
  accessKey: '',
  secretKey: '',
  basePrefix: '',
  presignedUrlExpiration: 3600,
  allowedMimeTypes: [],
  allowedPrefixes: [],
  blockedPrefixes: [],
  searchableMetadataFields: [],
};

// ─── Icons ───────────────────────────────────────────────────────────────────

function IconS3() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" strokeLinecap="round" strokeLinejoin="round" />
      <polyline points="22,6 12,13 2,6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconArrowLeft() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconPlus() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <line x1="12" y1="5" x2="12" y2="19" strokeLinecap="round" />
      <line x1="5" y1="12" x2="19" y2="12" strokeLinecap="round" />
    </svg>
  );
}

function IconX() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <line x1="18" y1="6" x2="6" y2="18" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="6" y1="6" x2="18" y2="18" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconTrash() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <polyline points="3 6 5 6 21 6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Tag Input Component ─────────────────────────────────────────────────────

function TagInput({
  label,
  items,
  onAdd,
  onRemove,
  placeholder,
  helperText,
}: {
  label: string;
  items: string[];
  onAdd: (item: string) => void;
  onRemove: (index: number) => void;
  placeholder?: string;
  helperText?: string;
}) {
  const [inputValue, setInputValue] = useState('');

  const handleAdd = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !items.includes(trimmed)) {
      onAdd(trimmed);
      setInputValue('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div>
      <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: tokens.colors.neutral[700], marginBottom: 4 }}>
        {label}
      </label>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          style={{
            flex: 1,
            padding: '8px 12px',
            fontSize: 14,
            borderRadius: tokens.borderRadius.md,
            border: `1px solid ${tokens.colors.neutral[300]}`,
            backgroundColor: tokens.colors.neutral[50],
            color: tokens.colors.neutral[900],
            outline: 'none',
          }}
        />
        <Button variant="ghost" size="sm" onClick={handleAdd} disabled={!inputValue.trim()}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><IconPlus /> Agregar</span>
        </Button>
      </div>
      {items.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {items.map((item, i) => (
            <span
              key={i}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '4px 10px',
                fontSize: 13,
                borderRadius: tokens.borderRadius.full,
                backgroundColor: tokens.colors.primary[100],
                color: tokens.colors.primary[700],
                fontWeight: 500,
              }}
            >
              {item}
              <button
                type="button"
                onClick={() => onRemove(i)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  color: tokens.colors.primary[600],
                }}
                aria-label={`Eliminar ${item}`}
              >
                <IconX />
              </button>
            </span>
          ))}
        </div>
      )}
      {helperText && (
        <p style={{ margin: '4px 0 0', fontSize: 12, color: tokens.colors.neutral[500] }}>{helperText}</p>
      )}
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export function S3ConfigPage() {
  const { agentId } = useParams<{ agentId: string }>();
  const token = localStorage.getItem(TENANT_TOKEN_KEY);

  const [existingConfig, setExistingConfig] = useState<S3Config | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState<FormData>({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Test connection state
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Delete confirmation
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // ─── Data Loading ────────────────────────────────────────────────────────

  const loadConfig = useCallback(async () => {
    if (!agentId || !token) return;
    setLoading(true);
    setError('');
    try {
      const data = await apiGet<S3Config | null>(
        `/api/agents/${agentId}/s3/config`,
        token
      );
      if (data) {
        setExistingConfig(data);
        setForm({
          bucketName: data.bucketName,
          region: data.region,
          authMode: data.authMode,
          accessKey: data.accessKey || '',
          secretKey: data.secretKey || '',
          basePrefix: data.basePrefix || '',
          presignedUrlExpiration: data.presignedUrlExpiration,
          allowedMimeTypes: data.allowedMimeTypes || [],
          allowedPrefixes: data.allowedPrefixes || [],
          blockedPrefixes: data.blockedPrefixes || [],
          searchableMetadataFields: data.searchableMetadataFields || [],
        });
      }
    } catch (e) {
      // 404 means no config exists yet, which is fine
      if ((e as { status?: number }).status !== 404) {
        setError((e as Error).message);
      }
    } finally {
      setLoading(false);
    }
  }, [agentId, token]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  // ─── Form Handlers ───────────────────────────────────────────────────────

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!form.bucketName.trim()) {
      errors.bucketName = 'El nombre del bucket es requerido';
    }
    if (!form.region) {
      errors.region = 'La región es requerida';
    }
    if (form.authMode === 'access-keys') {
      if (!form.accessKey.trim() && !existingConfig) {
        errors.accessKey = 'El Access Key es requerido';
      }
      if (!form.secretKey.trim() && !existingConfig) {
        errors.secretKey = 'El Secret Key es requerido';
      }
    }
    if (form.presignedUrlExpiration < 60) {
      errors.presignedUrlExpiration = 'Mínimo 60 segundos';
    }
    if (form.presignedUrlExpiration > 86400) {
      errors.presignedUrlExpiration = 'Máximo 86400 segundos (24 horas)';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm() || !agentId || !token) return;
    setSaving(true);
    setFormErrors({});
    try {
      const payload = {
        bucketName: form.bucketName.trim(),
        region: form.region,
        authMode: form.authMode,
        ...(form.authMode === 'access-keys' && form.accessKey.trim() ? { accessKey: form.accessKey.trim() } : {}),
        ...(form.authMode === 'access-keys' && form.secretKey.trim() ? { secretKey: form.secretKey.trim() } : {}),
        basePrefix: form.basePrefix.trim(),
        presignedUrlExpiration: form.presignedUrlExpiration,
        allowedMimeTypes: form.allowedMimeTypes,
        allowedPrefixes: form.allowedPrefixes,
        blockedPrefixes: form.blockedPrefixes,
        searchableMetadataFields: form.searchableMetadataFields,
      };

      if (existingConfig) {
        const updated = await apiPut<S3Config>(
          `/api/agents/${agentId}/s3/config`,
          payload,
          token,
          { successMessage: 'Configuración S3 actualizada' }
        );
        setExistingConfig(updated);
      } else {
        const created = await apiPost<S3Config>(
          `/api/agents/${agentId}/s3/config`,
          payload,
          token,
          { successMessage: 'Configuración S3 creada' }
        );
        setExistingConfig(created);
      }
    } catch (e) {
      setFormErrors({ _general: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!agentId || !token) return;
    setTesting(true);
    setTestResult(null);
    try {
      const result = await apiPost<{ success: boolean; message: string }>(
        `/api/agents/${agentId}/s3/config/test`,
        {
          bucketName: form.bucketName.trim(),
          region: form.region,
          authMode: form.authMode,
          ...(form.authMode === 'access-keys' ? { accessKey: form.accessKey.trim(), secretKey: form.secretKey.trim() } : {}),
        },
        token
      );
      setTestResult(result);
    } catch (e) {
      setTestResult({ success: false, message: (e as Error).message });
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async () => {
    if (!agentId || !token) return;
    setDeleting(true);
    try {
      await apiDelete(
        `/api/agents/${agentId}/s3/config`,
        token,
        { successMessage: 'Configuración S3 eliminada' }
      );
      setExistingConfig(null);
      setForm({ ...EMPTY_FORM });
      setDeleteModalOpen(false);
    } catch (e) {
      setError((e as Error).message);
      setDeleteModalOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  if (!token) return null;

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Link
          to={`/app/agents/${agentId}/plugins`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            color: tokens.colors.neutral[600],
            textDecoration: 'none',
            fontSize: 14,
            marginBottom: 12,
            transition: `color ${tokens.transitions.fast}`,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = tokens.colors.primary[600])}
          onMouseLeave={(e) => (e.currentTarget.style.color = tokens.colors.neutral[600])}
        >
          <IconArrowLeft />
          Volver a plugins
        </Link>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: tokens.borderRadius.lg,
                backgroundColor: tokens.colors.primary[100],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: tokens.colors.primary[600],
              }}
            >
              <IconS3 />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: 22, color: tokens.colors.neutral[900] }}>
                Configuración S3
              </h1>
              <p style={{ margin: 0, fontSize: 14, color: tokens.colors.neutral[500] }}>
                Conecta un bucket de Amazon S3 a este agente
              </p>
            </div>
          </div>
          {existingConfig && (
            <Button variant="ghost" size="md" onClick={() => setDeleteModalOpen(true)}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: tokens.colors.error.main }}>
                <IconTrash /> Eliminar configuración
              </span>
            </Button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div
          style={{
            padding: '12px 16px',
            marginBottom: 16,
            borderRadius: tokens.borderRadius.md,
            backgroundColor: tokens.colors.error.light,
            color: tokens.colors.error.dark,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48, color: tokens.colors.neutral[500] }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite', marginRight: 12 }}>
            <circle cx="12" cy="12" r="10" strokeDasharray="31.4 31.4" strokeLinecap="round" />
          </svg>
          Cargando configuración...
        </div>
      )}

      {/* Form */}
      {!loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {formErrors._general && (
            <div style={{ padding: '8px 12px', borderRadius: tokens.borderRadius.md, backgroundColor: tokens.colors.error.light, color: tokens.colors.error.dark, fontSize: 13 }}>
              {formErrors._general}
            </div>
          )}

          {/* Bucket Connection Section */}
          <Card variant="outlined" padding="lg">
            <h2 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: tokens.colors.neutral[900] }}>
              Conexión al Bucket
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Bucket Name */}
              <Input
                label="Nombre del Bucket"
                placeholder="mi-bucket-s3"
                value={form.bucketName}
                onChange={(e) => setForm({ ...form, bucketName: e.target.value })}
                error={formErrors.bucketName}
                fullWidth
              />

              {/* Region */}
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: tokens.colors.neutral[700], marginBottom: 4 }}>
                  Región
                </label>
                <select
                  value={form.region}
                  onChange={(e) => setForm({ ...form, region: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: 14,
                    borderRadius: tokens.borderRadius.md,
                    border: `1px solid ${formErrors.region ? tokens.colors.error.main : tokens.colors.neutral[300]}`,
                    backgroundColor: tokens.colors.neutral[50],
                    color: tokens.colors.neutral[900],
                    outline: 'none',
                  }}
                >
                  {AWS_REGIONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
                {formErrors.region && (
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: tokens.colors.error.dark }}>{formErrors.region}</p>
                )}
              </div>

              {/* Auth Mode Toggle */}
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: tokens.colors.neutral[700], marginBottom: 8 }}>
                  Modo de Autenticación
                </label>
                <div style={{ display: 'flex', gap: 0, borderRadius: tokens.borderRadius.md, overflow: 'hidden', border: `1px solid ${tokens.colors.neutral[300]}` }}>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, authMode: 'access-keys' })}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      fontSize: 14,
                      fontWeight: 500,
                      border: 'none',
                      cursor: 'pointer',
                      backgroundColor: form.authMode === 'access-keys' ? tokens.colors.primary[600] : tokens.colors.neutral[50],
                      color: form.authMode === 'access-keys' ? '#fff' : tokens.colors.neutral[700],
                      transition: `all ${tokens.transitions.fast}`,
                    }}
                  >
                    Access Keys
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, authMode: 'iam-role' })}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      fontSize: 14,
                      fontWeight: 500,
                      border: 'none',
                      borderLeft: `1px solid ${tokens.colors.neutral[300]}`,
                      cursor: 'pointer',
                      backgroundColor: form.authMode === 'iam-role' ? tokens.colors.primary[600] : tokens.colors.neutral[50],
                      color: form.authMode === 'iam-role' ? '#fff' : tokens.colors.neutral[700],
                      transition: `all ${tokens.transitions.fast}`,
                    }}
                  >
                    IAM Role
                  </button>
                </div>
              </div>

              {/* Access Keys Fields */}
              {form.authMode === 'access-keys' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <Input
                    label="Access Key"
                    type="password"
                    placeholder={existingConfig ? '••••••••••••' : 'AKIAIOSFODNN7EXAMPLE'}
                    value={form.accessKey}
                    onChange={(e) => setForm({ ...form, accessKey: e.target.value })}
                    error={formErrors.accessKey}
                    helperText={existingConfig ? 'Dejar vacío para mantener el valor actual' : undefined}
                    fullWidth
                  />
                  <Input
                    label="Secret Key"
                    type="password"
                    placeholder={existingConfig ? '••••••••••••' : 'wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY'}
                    value={form.secretKey}
                    onChange={(e) => setForm({ ...form, secretKey: e.target.value })}
                    error={formErrors.secretKey}
                    helperText={existingConfig ? 'Dejar vacío para mantener el valor actual' : undefined}
                    fullWidth
                  />
                </div>
              )}

              {/* IAM Role Info */}
              {form.authMode === 'iam-role' && (
                <div
                  style={{
                    padding: '12px 16px',
                    borderRadius: tokens.borderRadius.md,
                    backgroundColor: tokens.colors.info.light,
                    color: tokens.colors.info.dark,
                    fontSize: 13,
                  }}
                >
                  Se utilizará el IAM Role asignado a la instancia. Asegúrate de que el rol tenga permisos de lectura sobre el bucket configurado.
                </div>
              )}

              {/* Test Connection */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Button variant="ghost" size="md" onClick={handleTestConnection} disabled={testing || !form.bucketName.trim()}>
                  {testing ? 'Probando...' : 'Probar Conexión'}
                </Button>
                {testResult && (
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: testResult.success ? tokens.colors.success.dark : tokens.colors.error.dark,
                    }}
                  >
                    {testResult.success ? '✓ ' : '✗ '}{testResult.message}
                  </span>
                )}
              </div>
            </div>
          </Card>

          {/* URL & Prefix Section */}
          <Card variant="outlined" padding="lg">
            <h2 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: tokens.colors.neutral[900] }}>
              Opciones Generales
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Input
                label="Prefijo Base"
                placeholder="documents/"
                value={form.basePrefix}
                onChange={(e) => setForm({ ...form, basePrefix: e.target.value })}
                helperText="Prefijo raíz para todas las operaciones S3 (opcional)"
                fullWidth
              />
              <Input
                label="Expiración de URL Pre-firmada (segundos)"
                type="number"
                min={60}
                max={86400}
                value={form.presignedUrlExpiration}
                onChange={(e) => setForm({ ...form, presignedUrlExpiration: parseInt(e.target.value) || 3600 })}
                error={formErrors.presignedUrlExpiration}
                helperText="Tiempo de validez de los enlaces de descarga (60–86400 seg)"
                fullWidth
              />
            </div>
          </Card>

          {/* File Types Section */}
          <Card variant="outlined" padding="lg">
            <h2 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: tokens.colors.neutral[900] }}>
              Tipos de Archivo Permitidos
            </h2>
            <TagInput
              label="MIME Types"
              items={form.allowedMimeTypes}
              onAdd={(item) => setForm({ ...form, allowedMimeTypes: [...form.allowedMimeTypes, item] })}
              onRemove={(i) => setForm({ ...form, allowedMimeTypes: form.allowedMimeTypes.filter((_, idx) => idx !== i) })}
              placeholder="application/pdf, image/png, etc."
              helperText="Si está vacío, se permiten todos los tipos de archivo"
            />
          </Card>

          {/* Prefix Restrictions Section */}
          <Card variant="outlined" padding="lg">
            <h2 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: tokens.colors.neutral[900] }}>
              Restricciones de Prefijos
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <TagInput
                label="Prefijos Permitidos"
                items={form.allowedPrefixes}
                onAdd={(item) => setForm({ ...form, allowedPrefixes: [...form.allowedPrefixes, item] })}
                onRemove={(i) => setForm({ ...form, allowedPrefixes: form.allowedPrefixes.filter((_, idx) => idx !== i) })}
                placeholder="public/, shared/docs/"
                helperText="Directorios accesibles por el agente (vacío = todos)"
              />
              <TagInput
                label="Prefijos Bloqueados"
                items={form.blockedPrefixes}
                onAdd={(item) => setForm({ ...form, blockedPrefixes: [...form.blockedPrefixes, item] })}
                onRemove={(i) => setForm({ ...form, blockedPrefixes: form.blockedPrefixes.filter((_, idx) => idx !== i) })}
                placeholder="private/, internal/secrets/"
                helperText="Directorios que el agente no puede acceder"
              />
            </div>
          </Card>

          {/* Metadata Section */}
          <Card variant="outlined" padding="lg">
            <h2 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: tokens.colors.neutral[900] }}>
              Metadata Buscable
            </h2>
            <TagInput
              label="Campos de Metadata"
              items={form.searchableMetadataFields}
              onAdd={(item) => setForm({ ...form, searchableMetadataFields: [...form.searchableMetadataFields, item] })}
              onRemove={(i) => setForm({ ...form, searchableMetadataFields: form.searchableMetadataFields.filter((_, idx) => idx !== i) })}
              placeholder="project, category, author"
              helperText="Campos de metadata de S3 que el agente puede usar para búsquedas"
            />
          </Card>

          {/* Save Button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <Button variant="primary" size="md" onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando...' : existingConfig ? 'Actualizar Configuración' : 'Crear Configuración'}
            </Button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Eliminar Configuración S3"
        size="sm"
        footer={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" size="md" onClick={() => setDeleteModalOpen(false)} disabled={deleting}>
              Cancelar
            </Button>
            <Button variant="primary" size="md" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </div>
        }
      >
        <p style={{ margin: 0, color: tokens.colors.neutral[700] }}>
          ¿Estás seguro de que deseas eliminar la configuración S3 de este agente? El agente perderá acceso al bucket y esta acción no se puede deshacer.
        </p>
      </Modal>

      {/* CSS for spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default S3ConfigPage;
